import { Action, Reducer } from 'redux'
import { ActionsObservable, ofType } from 'redux-observable'
import { from, iif, of } from 'rxjs'
import { mergeMap, catchError, delay, map, merge, groupBy, pairwise, startWith } from 'rxjs/operators'
import { AppThunkAction } from '.'
import { ApiException, DeviceTwinOrHeartbeatRequestedType, DeviceTwinResponse, ModemsClient, ModemRequestDTO, ModemWithOrgSiteResponseDTO, ProblemDetails, ReportedTwinDTO, RelayApiClient, ModemDeviceTwinResponseDTO } from '../generated/ApiClient'
import { isComplete, isSimple } from '../generated/ApiClientExtensions'
import { actionCreators as snackbar, SnackbarAction } from './AlertSnackbarStore'

export interface ModemsState {
    isLoading: boolean
    // todo: there will probably need to be an error type if we can't find the modem
    modems: { [key: string]: ModemWithOrgSiteResponseDTO }
    // load device twins as a separate object so we can update them through server-side pushes
    deviceTwins: { [key: string]: { simpleTwin?: ReportedTwinDTO | undefined, completeTwin?: unknown | undefined } | undefined }
}

interface RequestModemsAction {
    type: 'modems/RequestModems'
}

interface ResponseModemsAction {
    type: 'modems/ReceiveModems'
    modems: ModemWithOrgSiteResponseDTO[]
}

interface RequestModemAction {
    type: 'modems/RequestModem'
    modemHostId: string
}

interface ResponseModemAction {
    type: 'modems/ReceiveModem'
    modem: ModemWithOrgSiteResponseDTO
}

interface UpdateModemPropertiesAction {
    type: 'modems/UpdateModemProperties'
    modem: ModemRequestDTO
}

interface RequestDeviceTwinPayload {
    modemHostId: string
    requestedType: DeviceTwinOrHeartbeatRequestedType
}

type RequestDeviceTwinAction = RequestDeviceTwinPayload & {
    type: 'modems/RequestDeviceTwin'
}

type RefreshDeviceTwinAction = RequestDeviceTwinPayload & {
    type: 'modems/RefreshDeviceTwin'
}

type CancelDeviceTwinAction = RequestDeviceTwinPayload & {
    type: 'modems/CancelDeviceTwin'
}

interface ResponseDeviceTwinAction {
    type: 'modems/ResponseDeviceTwin'
    twins: ModemDeviceTwinResponseDTO[]
}

type ModemAction = RequestModemsAction | ResponseModemsAction | RequestModemAction | ResponseModemAction
    | RequestDeviceTwinAction | RefreshDeviceTwinAction | ResponseDeviceTwinAction | CancelDeviceTwinAction
    | UpdateModemPropertiesAction

export const actionCreators = {
    requestModems: (): AppThunkAction<ModemAction> => async (dispatch, getState) => {
        const appState = getState()
        if (appState.modems) {
            dispatch({ type: 'modems/RequestModems' })
            const data = await new ModemsClient().getManyAll()
            dispatch({ type: 'modems/ReceiveModems', modems: data })
        }
    },
    requestModem: (modemHostId: string): AppThunkAction<ModemAction> => async (dispatch, getState) => {
        const appState = getState()
        if (appState.modems) {
            dispatch({ type: 'modems/RequestModem', modemHostId })
            const data = await new ModemsClient().get2(modemHostId)
            dispatch({ type: 'modems/ReceiveModem', modem: data })
        }
    },
    updateModemProperties: (modemHostId: string, modem: ModemRequestDTO): AppThunkAction<ModemAction | SnackbarAction> => async (dispatch, getState) => {
        const appState = getState()
        if (appState.modems && appState.modems.modems[modemHostId]) {
            try {
                const data = await new ModemsClient().update(modemHostId, modem)
                dispatch({ type: 'modems/ReceiveModem', modem: { ...appState.modems.modems[modemHostId], ...data } })
            }
            catch (error) {
                dispatch(snackbar.addError(error, undefined, 'Failed to save modem'))
            }
        }
    },
    requestTwin: (modemHostId: string, requestedType: DeviceTwinOrHeartbeatRequestedType): AppThunkAction<ModemAction> => async (dispatch, getState) => {
        dispatch({ type: 'modems/RequestDeviceTwin', modemHostId, requestedType })
    },
    requestManyTwins: (modemHostIds: string[], requestedType: DeviceTwinOrHeartbeatRequestedType): AppThunkAction<ModemAction> => async (dispatch, getState) => {
        const existingTwins = Object.entries(getState().modems?.deviceTwins ?? {}).filter(([_, o]) => o?.simpleTwin != undefined).map(([s, _]) => s)
        const data = await new ModemsClient().getDeviceTwins(requestedType, { modemIds: modemHostIds.filter(s => existingTwins.find(x => s === x) === undefined) })
        dispatch({ type: 'modems/ResponseDeviceTwin', twins: data })
    },
    cancelTwinRequest: (modemHostId: string): AppThunkAction<ModemAction> => async (dispatch, getState) => {
        dispatch({ type: 'modems/CancelDeviceTwin', modemHostId, requestedType: 0 })
    }
}

export const refreshTwinEpic = (action$: ActionsObservable<RequestDeviceTwinAction | RefreshDeviceTwinAction | CancelDeviceTwinAction>) => {
    const client = new ModemsClient()
    return action$.pipe(
        ofType('modems/RequestDeviceTwin', 'modems/RefreshDeviceTwin', 'modems/CancelDeviceTwin'),
        groupBy<RequestDeviceTwinAction | RefreshDeviceTwinAction | CancelDeviceTwinAction, any>(action => action.modemHostId),
        mergeMap(group =>
            group.pipe(
                startWith(undefined),
                pairwise<RequestDeviceTwinAction | RefreshDeviceTwinAction | CancelDeviceTwinAction | undefined>(),
                mergeMap(([earlier, action]) =>// don't fire if the latest is cancel or a cancel came in ahead of a refresh
                    iif(() => !((action!.type === 'modems/CancelDeviceTwin') || (earlier?.type === 'modems/CancelDeviceTwin' && action!.type === 'modems/RefreshDeviceTwin')),
                        from(client.getDeviceTwin(action!.modemHostId, action!.requestedType)).pipe(
                            map(data => ({ type: 'modems/ResponseDeviceTwin', twins: [data] })),
                            catchError((err, caught) => of(snackbar.addError(err, 'warning', 'Unable to load device twin data. This error may resolve itself'))),
                            // emit a second event later, which will retry the epic, operating like a poller
                            merge(of(({ ...action, type: 'modems/RefreshDeviceTwin' }))
                                .pipe(delay(60000)))
                        ))))),
    )
}

const unloadedState: ModemsState = { isLoading: false, modems: {}, deviceTwins: {} }

// currently this replaces any modem information if a modem is refreshed from the server
export const reducer: Reducer<ModemsState> = (state: ModemsState | undefined, incomingAction: Action): ModemsState => {
    if (state === undefined)
        return unloadedState

    const action = incomingAction as ModemAction
    switch (action.type) {
        case 'modems/RequestModems':
            return {
                ...state,
                isLoading: true
            }
        case 'modems/ReceiveModems':
            return {
                ...state,
                modems: {
                    ...state.modems,
                    ...Object.fromEntries(action.modems.map(s => [s.modemHostId, s]))
                },
                isLoading: false
            }
        case 'modems/ReceiveModem':
            return {
                ...state,
                modems: {
                    ...state.modems,
                    [action.modem.modemHostId]: action.modem
                },
                isLoading: false
            }
        case 'modems/ResponseDeviceTwin':
            const twins = Object.fromEntries(action.twins.map(s => [s.modemHostId, s.deviceTwin == undefined ? undefined : {
                simpleTwin: isSimple(s.deviceTwin) ? s.deviceTwin.Fields : undefined,
                completeTwin: isComplete(s.deviceTwin) ? JSON.parse(s.deviceTwin.Fields.item) : undefined
            }]))
            return {
                ...state,
                deviceTwins: {
                    ...state.deviceTwins,
                    ...twins
                }
            }
        default:
            return state
    }
}